const hasBase64Decoder = (): boolean => typeof atob === 'function'
const hasBase64Encoder = (): boolean => typeof btoa === 'function'

const toBase64 = (value: string): string => {
  if (hasBase64Encoder()) return btoa(value)
  throw new Error('Base64 encoder unavailable')
}

const fromBase64 = (value: string): string => {
  if (hasBase64Decoder()) return atob(value)
  throw new Error('Base64 decoder unavailable')
}

const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4))
  const binary = fromBase64(normalized + padding)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes.buffer
}

const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index] as number)
  }
  return toBase64(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const mapCredentialDescriptor = (descriptor: PublicKeyCredentialDescriptor): PublicKeyCredentialDescriptor => ({
  ...descriptor,
  id: typeof descriptor.id === 'string' ? base64UrlToBuffer(descriptor.id) : descriptor.id,
})

const parseCreationOptionsFallback = (optionsJson: Record<string, unknown>): PublicKeyCredentialCreationOptions => {
  const publicKey = { ...optionsJson } as unknown as PublicKeyCredentialCreationOptions
  if (typeof publicKey.challenge === 'string') {
    publicKey.challenge = base64UrlToBuffer(publicKey.challenge)
  }
  if (publicKey.user && typeof publicKey.user.id === 'string') {
    publicKey.user.id = base64UrlToBuffer(publicKey.user.id)
  }
  if (Array.isArray(publicKey.excludeCredentials)) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map(mapCredentialDescriptor)
  }
  return publicKey
}

const parseCreationOptions = (optionsJson: Record<string, unknown>): PublicKeyCredentialCreationOptions => {
  const parser = (PublicKeyCredential as unknown as {
    parseCreationOptionsFromJSON?: (value: Record<string, unknown>) => PublicKeyCredentialCreationOptions
  }).parseCreationOptionsFromJSON

  if (typeof parser === 'function') {
    return parser(optionsJson)
  }
  return parseCreationOptionsFallback(optionsJson)
}

const parseRequestOptionsFallback = (optionsJson: Record<string, unknown>): PublicKeyCredentialRequestOptions => {
  const publicKey = { ...optionsJson } as unknown as PublicKeyCredentialRequestOptions
  if (typeof publicKey.challenge === 'string') {
    publicKey.challenge = base64UrlToBuffer(publicKey.challenge)
  }
  if (Array.isArray(publicKey.allowCredentials)) {
    publicKey.allowCredentials = publicKey.allowCredentials.map(mapCredentialDescriptor)
  }
  return publicKey
}

const parseRequestOptions = (optionsJson: Record<string, unknown>): PublicKeyCredentialRequestOptions => {
  const parser = (PublicKeyCredential as unknown as {
    parseRequestOptionsFromJSON?: (value: Record<string, unknown>) => PublicKeyCredentialRequestOptions
  }).parseRequestOptionsFromJSON

  if (typeof parser === 'function') {
    return parser(optionsJson)
  }
  return parseRequestOptionsFallback(optionsJson)
}

const toRegistrationResponsePayload = (credential: PublicKeyCredential): Record<string, unknown> => {
  const credentialWithJson = credential as PublicKeyCredential & {
    toJSON?: () => Record<string, unknown>
  }
  if (typeof credentialWithJson.toJSON === 'function') {
    return credentialWithJson.toJSON() as unknown as Record<string, unknown>
  }

  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  }
}

export const isPasskeyRegistrationSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof PublicKeyCredential !== 'undefined' &&
  !!navigator.credentials?.create

export const createPasskeyRegistrationResponse = async (
  optionsJson: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  if (!isPasskeyRegistrationSupported()) {
    throw new Error('Passkeys are not supported on this device.')
  }

  const publicKey = parseCreationOptions(optionsJson)
  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null
  if (!credential) {
    throw new Error('Passkey creation was cancelled.')
  }

  return toRegistrationResponsePayload(credential)
}

const toAuthenticationResponsePayload = (credential: PublicKeyCredential): Record<string, unknown> => {
  const credentialWithJson = credential as PublicKeyCredential & {
    toJSON?: () => Record<string, unknown>
  }
  if (typeof credentialWithJson.toJSON === 'function') {
    return credentialWithJson.toJSON() as unknown as Record<string, unknown>
  }

  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  }
}

export const isPasskeyAuthenticationSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof PublicKeyCredential !== 'undefined' &&
  !!navigator.credentials?.get

export const createPasskeyAuthenticationResponse = async (
  optionsJson: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  if (!isPasskeyAuthenticationSupported()) {
    throw new Error('Passkeys are not supported on this device.')
  }

  const publicKey = parseRequestOptions(optionsJson)
  const credential = (await navigator.credentials.get({
    publicKey,
  })) as PublicKeyCredential | null
  if (!credential) {
    throw new Error('Passkey authentication was cancelled.')
  }

  return toAuthenticationResponsePayload(credential)
}
